// Транспорт раздела «Списки Bitrix»: реестр универсальных списков и их элементы.
// ТОЛЬКО ЧТЕНИЕ.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ
// Источник — зеркало списков Bitrix в своих таблицах (bxl_*), у него свой синк и своя
// отсечка (элементы переносятся с 01.01.2025). Складывать эти хуки в чужой файл значило
// бы однажды переиспользовать «покажи список» внутри раздела с другими правами.
//
// Записи здесь нет и появиться не должно: на полях списков в Bitrix висят
// бизнес-процессы, которые ставят задачи живым людям.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/bx-lists'

/** Сколько строк на странице. То же число, что у соседних разделов зеркала. */
export const РАЗМЕР_СТРАНИЦЫ = 50

/**
 * Тот же разбор ответа, что в api/client.ts и api/coordProjects.ts. Продублирован
 * намеренно: общего «api/base.ts» в проекте нет, и вводить его ради двадцати строк
 * значило бы поставить всем разделам общую точку отказа.
 */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 403) {
      throw new Error('раздел «Списки Bitrix» вашей роли не открыт: среди списков есть '
        + 'кадровые — компетенции сотрудников и рабочее время исполнителей')
    }
    // 400 от ядра — «так спрашивать нельзя» (поиск одной буквой). Пустая таблица
    // читалась бы как «ничего не найдено», а это неправда.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.why as string) || (rec.error as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────────── формы ответов ядра (см. bxl_api.py) ───────────────

/** Строка реестра — список Bitrix со счётами, посчитанными синком. */
export interface СписокРеестра {
  iblock_id: number
  /** 'lists' | 'bitrix_processes' | 'structure' | 'lists_socnet' */
  iblock_type: string
  name: string | null
  description: string | null
  active: boolean | null
  bizproc: boolean | null
  elements_total: number | null
  /** Сколько элементов с отсечки (cut_from) — ровно столько лежит в зеркале. */
  elements_since_cut: number | null
  cut_from: string | null
  fields_total: number | null
  /** Список уже зеркалится отдельной сущностью (свой синк и свой раздел). */
  mirrored_elsewhere: boolean
  /** Кадровый список (компетенции, рабочее время) — бейдж, НЕ фильтр. */
  kadrovoe: boolean
  synced_at: string | null
}

/** Колонка таблицы элементов — ядро подобрало её по заполненности по всему списку. */
export interface Колонка {
  code: string
  name: string
  type: string | null
  multiple: boolean
}

/**
 * Значение ячейки. `masked` — значение намеренно не перенесено синком (в нём то, чему
 * в зеркале не место). Пустая ячейка читалась бы как «ничего не было», и это неправда —
 * экран обязан сказать «скрыто» словами.
 */
export interface Ячейка {
  text: string | null
  masked: boolean
}

export interface ЭлементСтрока {
  id: number
  name: string | null
  active_from: string | null
  props_filled: number | null
  files_count: number | null
  masked_count: number | null
  values: Record<string, Ячейка | undefined>
}

export interface СтраницаЭлементов {
  rows: ЭлементСтрока[]
  total: number
  next: number | null
  limit: number
  start: number
  columns: Колонка[]
  list: { iblock_id: number; name: string | null; cut_from: string | null }
}

export interface ПолеКарточки {
  code: string
  name: string
  type: string | null
  multiple: boolean
  masked: boolean
  values: string[]
}

export interface КарточкаЭлемента {
  list: { iblock_id: number; name: string | null }
  element: {
    id: number
    name: string | null
    code: string | null
    active_from: string | null
    active_to: string | null
    created_by: number | null
    bp_published: boolean | null
    props_filled: number | null
    files_count: number | null
    masked_count: number | null
    is_deleted: boolean
    first_seen_at: string | null
    synced_at: string | null
  }
  fields: ПолеКарточки[]
}

// ─────────────────────────────── хуки ───────────────────────────────

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

export function useРеестрСписков() {
  return useQuery<{ rows: СписокРеестра[]; total: number }>({
    queryKey: ['bx-lists', 'registry'],
    queryFn: () => запрос('/registry'),
    ...ОБЩЕЕ,
  })
}

/** Страница элементов. Без выбранного списка не ходим вовсе, а не спрашиваем «ничего». */
export function useЭлементы(iblock: number | null, start: number, q: string) {
  return useQuery<СтраницаЭлементов>({
    queryKey: ['bx-lists', 'rows', iblock, start, q],
    queryFn: () => {
      const p = new URLSearchParams({ iblock: String(iblock) })
      if (start) p.set('start', String(start))
      if (q) p.set('q', q)
      return запрос<СтраницаЭлементов>('/rows?' + p.toString())
    },
    enabled: iblock !== null,
    ...ОБЩЕЕ,
  })
}

export function useЭлемент(iblock: number | null, id: number | null) {
  return useQuery<КарточкаЭлемента>({
    queryKey: ['bx-lists', 'row', iblock, id],
    queryFn: () => запрос<КарточкаЭлемента>(`/row?iblock=${iblock}&id=${id}`),
    enabled: iblock !== null && id !== null,
    ...ОБЩЕЕ,
  })
}

/** Тип списка — по-русски. Незнакомый тип показываем как есть, а не прячем. */
export const ТИП_СПИСКА: Record<string, string> = {
  lists: 'список',
  bitrix_processes: 'процесс',
  lists_socnet: 'список группы',
  structure: 'структура',
}
